import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import OtpModal from '../components/OtpModal';
import toast from 'react-hot-toast';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [otpOpen, setOtpOpen] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const { login, loginWithGoogle, resetPassword, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  
  const { register, handleSubmit, formState: { errors }, getValues } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (loginMethod === 'email') {
        await login(data.email, data.password);
        navigate('/');
      } else {
        // Trigger OTP flow
        setPendingPhone(data.phone);
        setOtpOpen(true);
      }
    } catch (error) {
      console.error('Login error:', error);
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = async (confirmationResult, code) => {
    await verifyOtp(confirmationResult, code);
    setOtpOpen(false);
    navigate('/');
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (error) {
      console.error('Google login error:', error);
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async () => {
    const email = getValues('email');
    if (!email) {
      toast.error('Please enter your email first');
      return;
    }
    
    try {
      await resetPassword(email);
    } catch (error) {
      console.error('Password reset error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max">
          <div className="max-w-md mx-auto">
            <motion.div
              className="bg-white rounded-lg shadow-xl overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-8">
                {/* Header */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-serif font-light text-deep-brown mb-2">
                    Welcome Back
                  </h1>
                  <p className="text-warm-gray">
                    Sign in to your account to continue shopping
                  </p>
                </div>

                {/* Login Method Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                  <button
                    type="button"
                    onClick={() => setLoginMethod('email')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                      loginMethod === 'email'
                        ? 'bg-white text-deep-brown shadow-sm'
                        : 'text-warm-gray hover:text-deep-brown'
                    }`}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMethod('phone')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                      loginMethod === 'phone'
                        ? 'bg-white text-deep-brown shadow-sm'
                        : 'text-warm-gray hover:text-deep-brown'
                    }`}
                  >
                    Phone
                  </button>
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {loginMethod === 'email' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Please enter a valid email'
                            }
                          })}
                          className="input-field"
                          placeholder="Enter your email"
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          {...register('password', {
                            required: 'Password is required',
                            minLength: {
                              value: 6,
                              message: 'Password must be at least 6 characters'
                            }
                          })}
                          className="input-field"
                          placeholder="Enter your password"
                        />
                        {errors.password && (
                          <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-deep-brown mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          {...register('phone', {
                            required: 'Phone number is required',
                            pattern: {
                              value: /^[6-9]\d{9}$/,
                              message: 'Please enter a valid 10-digit phone number'
                            }
                          })}
                          className="input-field"
                          placeholder="Enter your phone number"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                        )}
                      </div>
                      <p className="text-xs text-warm-gray">We’ll send an OTP to verify your number.</p>
                    </>
                  )}

                  {/* Forgot Password */}
                  {loginMethod === 'email' && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="text-sm text-warm-gray hover:text-deep-brown transition-colors duration-200"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98 }}
                  >
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </motion.button>
                </form>

                {/* Divider */}
                <div className="my-6 flex items-center">
                  <div className="flex-1 border-t border-warm-gray/20"></div>
                  <span className="px-4 text-sm text-warm-gray">or</span>
                  <div className="flex-1 border-t border-warm-gray/20"></div>
                </div>

                {/* Google Login */}
                <motion.button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center space-x-3 py-3 px-4 border border-warm-gray/30 rounded-none text-deep-brown hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: isLoading ? 1 : 1.02 }}
                  whileTap={{ scale: isLoading ? 1 : 0.98 }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-medium">Continue with Google</span>
                </motion.button>

                {/* Register Link */}
                <div className="mt-6 text-center">
                  <p className="text-warm-gray">
                    Don't have an account?{' '}
                    <Link
                      to="/register"
                      className="text-deep-brown hover:text-warm-gray transition-colors duration-200 font-medium"
                    >
                      Create Account
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        onSendOtp={sendOtp}
        onVerifyOtp={handleVerifyOtp}
        phone={pendingPhone}
        title="Verify your phone"
        subtitle={`Enter the 6-digit OTP sent to ${pendingPhone}`}
        recaptchaId="login-recaptcha"
      />
    </div>
  );
};

export default Login;